import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ReportRangeDateField from "./ReportRangeDateField";
import Select from "../../../components/Form/Select";
import ButtonCustom from "../../../components/Buttons/Button";
import { useState } from "react";
import ModalCampaingLogs from "../../NewAdsCampaign/components/CampaignLogs/ModalCampaingLogs";
import ModalCustomReport from "../../../components/Modal/ModalCustomReport";
import {
  BUTTON_FILTER,
  DATE_SHORT_2,
  HISTORICAL_CHANGES,
  REPORT_BREAKDOWN_PLACEHOLDER,
} from "../../../i18n/keysTranslations";
import { useFormContext } from "react-hook-form";
import { useTranslationApp } from "../../../lib/i18next";
import { optionsAdsCampaignBreakdownsReport } from "../../../utils/options";
import { BOX_SHADOW_THEME, INPUT_COLOR_BORDER } from "../../../utils/colors";
import { gallerySecondaryToolbarIconSx } from "../../../components/Gallery/galleryIconButtonStyles";
import { BORDER_RADIUS } from "../../../utils/constants";
import IconButton from "../../../components/Buttons/IconButton";
import HistoryIcon from "@mui/icons-material/History";
import { useReportSnapshot } from "../../../features/reportPdf/ReportSnapshotContext";
import CampaignReportPdfExportButton from "./CampaignReportPdfExportButton";

const ConfigurationReport = ({
  isPdfReportReady,
  filteredLogs,
  summaryText,
  learnings,
}) => {
  const [isModalLogsOpen, setIsModalLogsOpen] = useState(false);
  const [isCustomReportModalOpen, setIsCustomReportModalOpen] = useState(false);

  const {
    control,
    formState: { errors },
    campaign,
    campaignID,
    isGeneratingReport,
    getDetailedCampaignReport,
    reportConfiguration,
  } = useFormContext();

  const { t } = useTranslationApp();
  const reportSnapshot = useReportSnapshot();

  const campaignTitle =
    campaign?.platformsData?.[campaign?.platforms?.[0]]?.name || campaign?.name;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        pt: reportSnapshot ? 0 : 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography
          variant="h5"
          noWrap
          title={campaignTitle}
          sx={{
            fontWeight: 600,
          }}
        >
          {campaignTitle}
        </Typography>
        <Box
          data-pdf-hide
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: { xs: "flex-start", sm: "flex-end" },
          }}
        >
          {!reportSnapshot ? (
            <Box
              sx={(theme) =>
                gallerySecondaryToolbarIconSx({
                  theme,
                  buttonSize: 40,
                  iconSvgFontSize: 20,
                })
              }
            >
              <CampaignReportPdfExportButton
                filteredLogs={filteredLogs}
                summaryText={summaryText}
                learnings={learnings}
                isReady={isPdfReportReady}
                disabled={!campaignID}
                onOpenCustomReport={() => setIsCustomReportModalOpen(true)}
              />
            </Box>
          ) : null}
          <IconButton
            edge="end"
            onClick={() => {
              setIsModalLogsOpen(true);
            }}
            tooltipText={HISTORICAL_CHANGES}
          >
            <HistoryIcon
              sx={{
                width: 26,
                height: 26,
              }}
            />
          </IconButton>
          <Typography variant="body1" color="text.secondary">
            {t(DATE_SHORT_2, { date: new Date() })}
          </Typography>
        </Box>
      </Box>

      {!reportSnapshot && !isGeneratingReport && (
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", md: "flex-start" },
            justifyContent: { xs: "flex-start", md: "space-between" },
            width: "100%",
            gap: 2,
            flexWrap: { xs: "wrap", sm: "nowrap" },
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              width: "100%",
              backgroundColor: "fields.background",
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              boxShadow: BOX_SHADOW_THEME,
              borderRadius: BORDER_RADIUS,
              p: 3,
              alignItems: { xs: "stretch", md: "flex-start" },
            }}
          >
            <Box
              sx={{
                flex: 2,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                width: { xs: "100%", md: "auto" },
              }}
            >
              <ReportRangeDateField
                showStartNowButton={true}
                control={control}
                name="dateRange"
                sx={{
                  width: "100%",
                  flexGrow: 1,
                }}
                sxField={{
                  backgroundColor: "fields.background",
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  ".MuiInputBase-root": {
                    height: 60,
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                width: { xs: "100%", md: "auto" },
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {t(REPORT_BREAKDOWN_PLACEHOLDER)}
              </Typography>
              <Select
                name="breakdown"
                variant="filled"
                fullWidth
                doTranslate={true}
                options={optionsAdsCampaignBreakdownsReport}
                control={control}
                errors={errors}
                sx={{
                  maxWidth: "100%",
                  ".MuiInputBase-root": {
                    height: 60,
                  },
                }}
              />
            </Box>
            <ButtonCustom
              color="secondary"
              onClick={() => getDetailedCampaignReport(true)}
              sx={{
                width: { xs: "100%", md: 130 },
                minWidth: { xs: "100%", md: 130 },
                alignSelf: { xs: "stretch", md: "flex-end" },
                height: 63,
              }}
            >
              {t(BUTTON_FILTER)}
            </ButtonCustom>
          </Box>
        </Box>
      )}
      {!reportSnapshot ? (
        <>
          <ModalCampaingLogs
            modalOpen={isModalLogsOpen}
            onCloseModal={() => setIsModalLogsOpen(false)}
          />
          <ModalCustomReport
            modalOpen={isCustomReportModalOpen}
            onCloseModal={() => setIsCustomReportModalOpen(false)}
            dateRange={reportConfiguration}
            campaignID={campaignID}
          />
        </>
      ) : null}
    </Box>
  );
};

export default ConfigurationReport;
